/**
 * Unified Status Chip Component
 *
 * Provides consistent status chip styling across all tables and pages
 */

import React from 'react';
import { Box, Chip, ChipProps, alpha } from '@mui/material';

type StatusChipType =
  | 'device'
  | 'registration'
  | 'dps'
  | 'dpsEnv'
  | 'access'
  | 'gsm'
  | 'deployment'
  | 'generic';

interface StatusChipProps extends Omit<ChipProps, 'color'> {
  /** Raw status value. STATUS_CONFIGS below lists the words each type knows;
   *  anything else falls through to a neutral chip labelled with the value. */
  status: string;
  type?: StatusChipType;
}

/**
 * Tone encodes *health only*, never which column the chip sits in. Four tones,
 * one shape, one weight - so a row of chips reads as one system and colour only
 * fires when something actually needs attention.
 *
 * - good      (green)  operating as intended: Online, Verified, Assigned
 * - bad       (red)    a fault the operator must act on: Offline, Rejected, Failed
 * - attention (amber)  waiting on someone, or drifting: Pending, Expiring soon
 * - neutral   (grey)   a fact, not a verdict: Unknown, Not enrolled, No SIM
 */
type Tone = 'good' | 'bad' | 'attention' | 'neutral';

type StatusConfig = {
  tone: Tone;
  label: string;
};

/**
 * Looked up as `type:status` first, then as the bare status. Prefix a key when
 * the word belongs to one column's vocabulary (`gsm:enabled`, `dpsEnv:UAT`);
 * leave it bare when it means the same thing everywhere (`pending`). No bare
 * and prefixed pair currently collides - the prefix reserves the word so a
 * later column can give it its own tone without disturbing the bare entry.
 */
const STATUS_CONFIGS: Record<string, StatusConfig> = {
  // Device status
  online: { tone: 'good', label: 'Online' },
  offline: { tone: 'bad', label: 'Offline' },
  unknown: { tone: 'neutral', label: 'Unknown' },
  active: { tone: 'good', label: 'Active' },
  inactive: { tone: 'bad', label: 'Inactive' },

  // Registration status
  verified: { tone: 'good', label: 'Verified' },
  pending: { tone: 'attention', label: 'Pending' },
  rejected: { tone: 'bad', label: 'Rejected' },
  enrolled: { tone: 'neutral', label: 'Enrolled' },
  unverified_suspicious: { tone: 'bad', label: 'Suspicious' },

  // Azure DPS enrollment. Labelled "Assigned" rather than "Enrolled" so it does
  // not collide with the registration status of the same name.
  'dps:assigned': { tone: 'good', label: 'Assigned' },
  'dps:already_enrolled': { tone: 'good', label: 'Already assigned' },
  'dps:failed': { tone: 'bad', label: 'Failed' },
  'dps:not_enrolled': { tone: 'neutral', label: 'Not enrolled' },

  // Azure DPS environment. Which environment a device points at is a fact, not
  // a health signal, so every known value is neutral.
  'dpsEnv:Production': { tone: 'neutral', label: 'Production' },
  'dpsEnv:UAT': { tone: 'neutral', label: 'UAT' },
  'dpsEnv:DFO': { tone: 'neutral', label: 'DFO' },
  'dpsEnv:Unknown': { tone: 'attention', label: 'Unknown' },

  // Access status (Non-TPM)
  'access:active_access': { tone: 'good', label: 'Active' },
  'access:approved': { tone: 'good', label: 'Approved' },
  expired: { tone: 'bad', label: 'Expired' },
  expiring_soon: { tone: 'attention', label: 'Expiring soon' },
  needs_review: { tone: 'attention', label: 'Needs review' },

  // GSM modem. Shipping without a SIM is a configuration choice, not a fault.
  'gsm:enabled': { tone: 'neutral', label: 'SIM' },
  'gsm:disabled': { tone: 'neutral', label: 'No SIM' },

  // Deployment target: the Outcome once reported, else the live command status
  // ("pending" and "expired" use the bare entries above).
  'deployment:succeeded': { tone: 'good', label: 'Succeeded' },
  'deployment:failed': { tone: 'bad', label: 'Failed' },
  'deployment:executing': { tone: 'attention', label: 'In progress' },
};

/** Exported so statusChipContrast.test.ts covers exactly the tones used here. */
export const TONE_PALETTE_KEY = {
  good: 'success',
  bad: 'error',
  attention: 'warning',
} as const;

// Static, so they live here rather than being rebuilt for every chip in a page.
const DOT_ROW_SX = { display: 'inline-flex', alignItems: 'center', gap: 0.75 } as const;
const DOT_SX = { width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor', flexShrink: 0 } as const;

/**
 * StatusChip - unified status chip. `type` selects the column's vocabulary,
 * `status` is the raw value from the API.
 *
 * @example
 * <StatusChip status="online" type="device" />
 * <StatusChip status="verified" type="registration" />
 */
export const StatusChip: React.FC<StatusChipProps> = ({
  status,
  type = 'generic',
  label,
  size = 'small',
  sx,
  ...chipProps
}) => {
  const config: StatusConfig =
    STATUS_CONFIGS[`${type}:${status}`] ||
    STATUS_CONFIGS[status] || { tone: 'neutral', label: status };

  // Only the device status column carries a dot. Repeating it on every chip in
  // a row turns the table into a field of glyphs and stops it marking anything.
  const showDot = type === 'device';
  const text = label || config.label;

  return (
    <Chip
      label={
        showDot ? (
          <Box component="span" sx={DOT_ROW_SX}>
            <Box component="span" sx={DOT_SX} />
            {text}
          </Box>
        ) : (
          text
        )
      }
      size={size}
      variant="filled"
      {...chipProps}
      sx={[
        (theme) => {
          const paletteColor =
            config.tone === 'neutral' ? null : theme.palette[TONE_PALETTE_KEY[config.tone]];
          const accent = paletteColor ? paletteColor.main : theme.palette.text.secondary;
          const text = paletteColor
            ? paletteColor[theme.palette.mode === 'dark' ? 'light' : 'dark']
            : theme.palette.text.secondary;

          return {
            height: 22,
            borderRadius: '6px',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: 0,
            maxWidth: '100%',
            color: text,
            backgroundColor: alpha(accent, theme.palette.mode === 'dark' ? 0.2 : 0.12),
            border: `1px solid ${alpha(accent, theme.palette.mode === 'dark' ? 0.35 : 0.24)}`,
            boxShadow: 'none',
            // The global MuiChip theme scales chips on hover, which makes table
            // rows twitch as the pointer crosses them. Status chips are labels,
            // not controls, so they hold still.
            '&:hover': { transform: 'none' },
            '& .MuiChip-label': { px: 0.875, overflow: 'hidden', textOverflow: 'ellipsis' },
          };
        },
        // Callers keep the last word; array form avoids clobbering the base.
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
};
